import { useEffect, useMemo, useState } from 'react';
import { Info, RotateCw } from 'lucide-react';
import { useMyMotivationFacts } from '@tark/shared/hooks';
import {
  availableMotivationDisciplines,
  buildMotivationRows,
  hasMotivationReconciliationNotice,
  isMotivationNotFound,
} from '@tark/shared/lib';
import type { MotivationDiscipline } from '@tark/shared/types';

const MOTIVATION_UI_ENABLED = import.meta.env.VITE_MOTIVATION_P1A_UI_ENABLED === 'true';
const EVIDENCE_COPY = 'Verified results have full confirmation. Casual results are accepted play recorded with lighter confirmation. They stay separate so your progress is clear.';
let rememberedDiscipline: MotivationDiscipline | null = null;

export function MotivationSummary() {
  const query = useMyMotivationFacts(MOTIVATION_UI_ENABLED);
  const facts = query.data?.items ?? [];
  const disciplines = useMemo(() => availableMotivationDisciplines(facts), [facts]);
  const [discipline, setDiscipline] = useState<MotivationDiscipline>('SINGLES');
  const [infoOpen, setInfoOpen] = useState(false);

  useEffect(() => {
    if (!disciplines.length) return;
    setDiscipline(rememberedDiscipline && disciplines.includes(rememberedDiscipline)
      ? rememberedDiscipline : disciplines[0]);
  }, [disciplines]);

  const rows = useMemo(() => buildMotivationRows(facts, discipline), [discipline, facts]);
  const chooseDiscipline = (next: MotivationDiscipline) => {
    rememberedDiscipline = next;
    setDiscipline(next);
  };

  if (!MOTIVATION_UI_ENABLED || (query.isError && isMotivationNotFound(query.error))) return null;

  return (
    <section className="border-t border-border pt-5" data-testid="motivation-summary">
      <div className="flex min-h-11 items-center justify-between gap-3">
        <h2 className="text-xl font-bold text-text">Your play</h2>
        <button
          type="button"
          onClick={() => setInfoOpen(value => !value)}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-border text-primary transition-colors hover:bg-surface-elevated"
          aria-label="About Verified and Casual results"
          aria-expanded={infoOpen}
        >
          <Info className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {infoOpen ? (
        <div className="mt-3 rounded-lg border border-border bg-surface px-4 py-3 text-sm leading-6 text-text-muted">
          {EVIDENCE_COPY}
        </div>
      ) : null}

      {query.isLoading ? (
        <div className="flex min-h-24 items-center justify-center text-primary" aria-label="Loading your progress">
          <RotateCw className="h-5 w-5 animate-spin" aria-hidden="true" />
        </div>
      ) : null}

      {query.isError && !isMotivationNotFound(query.error) ? (
        <div className="mt-3 flex min-h-20 flex-col items-start justify-center gap-2 border-y border-border py-3">
          <p className="text-sm font-semibold text-text">Your progress could not be loaded.</p>
          <button type="button" onClick={() => void query.refetch()} className="text-sm font-semibold text-primary">
            Retry
          </button>
        </div>
      ) : null}

      {query.isSuccess && facts.length === 0 ? (
        <p className="mt-3 rounded-lg bg-surface px-4 py-5 text-sm font-medium text-text-muted">
          Your progress will appear after an eligible result is recorded.
        </p>
      ) : null}

      {query.isSuccess && rows.length ? (
        <div className="mt-4 space-y-5">
          {disciplines.length > 1 ? (
            <div className="grid h-11 grid-cols-2 rounded-lg border border-border bg-surface p-1" role="tablist" aria-label="Match discipline">
              {disciplines.map(option => (
                <button
                  key={option}
                  type="button"
                  role="tab"
                  aria-selected={discipline === option}
                  onClick={() => chooseDiscipline(option)}
                  className={`rounded-md text-sm font-bold transition-colors ${
                    discipline === option ? 'bg-primary text-white' : 'text-text-muted hover:text-text'
                  }`}
                >
                  {option === 'SINGLES' ? 'Singles' : 'Doubles'}
                </button>
              ))}
            </div>
          ) : null}

          {(['Record', 'Patterns', 'Personal best'] as const).map(section => {
            const sectionRows = rows.filter(row => row.section === section);
            if (!sectionRows.length) return null;
            return (
              <div key={section}>
                <h3 className="mb-1 text-xs font-bold uppercase text-text-subtle">{section}</h3>
                <div className="border-y border-border">
                  {sectionRows.map((row, index) => (
                    <div
                      key={row.code}
                      className={`flex min-h-14 items-center gap-4 py-2.5 ${
                        index < sectionRows.length - 1 ? 'border-b border-border' : ''
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-text">{row.label}</p>
                        {row.detail ? <p className="mt-0.5 text-xs text-text-subtle">{row.detail}</p> : null}
                      </div>
                      <p className="max-w-[56%] text-right text-sm font-bold tabular-nums text-text-muted">{row.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {hasMotivationReconciliationNotice(facts, discipline) ? (
            <p className="text-xs leading-5 text-text-subtle">
              Some recorded outcomes, such as walkovers or shortened retirements, do not count toward progress.
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
